import Chip from '@/components/chip';
import { ColumnRenderInfo } from '@/components/table';
import { TextWithTooltip } from '@/components/text-with-tooltip';
import Tooltip from '@/components/tooltip';
import { TableDataType } from '../_types';
import { TrackedAnchor } from '@/components/ancher';
import { LINKS } from '@/constants';

const ColumnRenderInfoList: ColumnRenderInfo<TableDataType>[] = [
  {
    key: 'geneName',
    header: (
      <span>
        Gene <sup className="text-[10px]">1</sup>
      </span>
    ),
    render: (value: string, row?: TableDataType) => {
      if (!value) return <span>-</span>;
      if (!row) return <span className="text-black underline">{value}</span>;

      return (
        <div className="flex min-w-0 items-center gap-2">
          <TrackedAnchor
            label={`result_item_link_${value}`}
            position="main_content"
            href={`${LINKS.GENE_BROWSER_GENE}/${value}`}
            className="min-w-0 flex-shrink truncate text-black underline"
            title={value}
          >
            {value}
          </TrackedAnchor>
          <div className="flex flex-shrink-0 items-center gap-1">
            {row.wesDeadzone === 'o' && (
              <Tooltip content="Deadzone in 3B-EXOME">
                <Chip
                  label="E"
                  className="bg-3b-error-light text-3b-error-medium"
                />
              </Tooltip>
            )}
            {row.wgsDeadzone === 'o' && (
              <Tooltip content="Deadzone in 3B-GENOME">
                <Chip
                  label="G"
                  className="bg-3b-yellow-light-200 text-3b-yellow-light-700"
                />
              </Tooltip>
            )}
          </div>
        </div>
      );
    },
    headerStyle:
      'w-[180px] min-w-[180px] max-w-[180px] sticky left-0 z-10 bg-3b-background-medium shadow-2xl',
    cellStyle:
      'w-[180px] min-w-[180px] max-w-[180px] sticky left-0 z-10 bg-white shadow-2xl',
  },
  {
    key: 'coverage20x',
    header: (
      <span>
        3B-EXOME <br />
        coverage <sup className="text-[10px]">2</sup>
      </span>
    ),
    render: (value: string, row?: TableDataType) => {
      // WES에 포함되지 않은 유전자인 경우
      if (row?.capturedByWes === 'x') {
        return (
          <span className="text-3b-text-disabled">
            Not captured <sup className="text-[10px]">4</sup>
          </span>
        );
      }

      // 커버리지가 0%인 경우 빨간색으로 표시
      if (Number(value) === 0) {
        return <span className="text-red-500">0%</span>;
      }

      // 정상적인 커버리지 값 표시
      return <span className="text-black">{parseFloat(value)}%</span>;
    },
    headerStyle: 'min-w-[120px] flex-1',
    cellStyle: 'min-w-[120px] flex-1',
  },
  {
    key: 'wesExonsDeadzone',
    header: (
      <span>
        3B-EXOME <br />
        deadzone exons
      </span>
    ),
    render: (value: string) => {
      if (value === 'NA' || !value) {
        return <span className="text-3b-text-disabled">N/A</span>;
      }
      if (value === 'whole_gene') {
        return <span className="text-3b-error-primary">Whole gene</span>;
      }
      return <TextWithTooltip className="text-black">{value}</TextWithTooltip>;
    },
    headerStyle: 'min-w-[200px] max-w-[200px]',
    cellStyle: 'min-w-[200px] max-w-[200px]',
  },
  {
    key: 'coverage10x',
    header: (
      <span>
        3B-GENOME <br />
        coverage <sup className="text-[10px]">3</sup>
      </span>
    ),
    render: (value: string) => {
      if (Number(value) === 0) {
        return <span className="text-red-500">0%</span>;
      }

      return <span className="text-black">{parseFloat(value)}%</span>;
    },
    headerStyle: 'min-w-[120px] flex-1',
    cellStyle: 'min-w-[120px] flex-1',
  },
  {
    key: 'wgsExonsDeadzone',
    header: (
      <span>
        3B-GENOME <br />
        deadzone exons
      </span>
    ),
    render: (value: string) => {
      if (value === 'NA' || !value) {
        return <span className="text-3b-text-disabled">N/A</span>;
      }
      if (value === 'whole_gene') {
        return <span className="text-3b-error-primary">Whole gene</span>;
      }
      return <TextWithTooltip className="text-black">{value}</TextWithTooltip>;
    },
    headerStyle: 'min-w-[200px] max-w-[200px]',
    cellStyle: 'min-w-[200px] max-w-[200px]',
  },
];

const TABLE_OFFSET_TOP = 160;
const TABLE_OFFSET_TOP_MOBILE = 100;

const ITEMS_PER_PAGE = 50;

export {
  ColumnRenderInfoList,
  ITEMS_PER_PAGE,
  TABLE_OFFSET_TOP,
  TABLE_OFFSET_TOP_MOBILE,
};
